import { useState } from "react";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import type { TeamPublic, TeamDeleteImpact } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, X, ArrowRightLeft, Trash2 } from "lucide-react";
import { TeamDeleteDialog } from "@/components/team-delete-dialog";

interface TeamTabBarProps {
  teams: TeamPublic[];
  activeTeamId: string | null;
  onSelectTeam: (id: string) => void;
  onDeleteTeam: (id: string) => void;
  onCreateTeam: (isTrial: boolean) => void;
  onConvertTeam?: (id: string) => void;
  canEdit: boolean;
  saving: boolean;
}

// 184: one team tab. A tab is BOTH a dnd-kit draggable (rank reorder within its
// team_type partition) and a droppable (accepts a team-tab drop for reorder, or a
// player drop to switch+select). The actual reorder / player-drop routing happens
// centrally in inline-team-builder's onDragEnd; this just registers the node and
// keeps the existing tap interactions (select / convert / delete).
function TeamTab({
  team,
  isActive,
  partitionSize,
  canEdit,
  saving,
  checkingImpact,
  onSelectTeam,
  onConvertTeam,
  onDeleteClick,
}: {
  team: TeamPublic;
  isActive: boolean;
  partitionSize: number;
  canEdit: boolean;
  saving: boolean;
  checkingImpact: string | null;
  onSelectTeam: (id: string) => void;
  onConvertTeam?: (id: string) => void;
  onDeleteClick: (team: TeamPublic) => void;
}) {
  const isTrial = team.team_type === "trial";
  // 087: only allow reorder within a team_type that has more than one team.
  const canReorder = canEdit && partitionSize > 1;

  const drag = useDraggable({
    id: `team-tab-${team.id}`,
    data: { kind: "team-tab", teamId: team.id, teamType: team.team_type, label: team.name },
    disabled: !canReorder,
  });
  const drop = useDroppable({
    id: `team-tab-drop-${team.id}`,
    data: { kind: "team-tab", teamId: team.id, teamType: team.team_type },
  });
  const setRefs = (node: HTMLElement | null) => {
    drag.setNodeRef(node);
    drop.setNodeRef(node);
  };

  const baseClasses = "flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm transition-colors";
  const trialBorder = isTrial ? "border-dashed" : "";

  let stateClasses: string;
  if (isActive) {
    stateClasses = isTrial
      ? "bg-trial text-trial-foreground border-trial"
      : "bg-primary text-primary-foreground border-primary";
  } else if (drop.isOver) {
    stateClasses = isTrial ? "bg-muted border-trial" : "bg-muted border-primary";
  } else {
    stateClasses = "bg-background border-input hover:bg-muted/50";
  }

  return (
    <div
      ref={setRefs}
      {...drag.listeners}
      {...drag.attributes}
      className={`${baseClasses} ${trialBorder} ${stateClasses} ${canReorder ? "cursor-grab active:cursor-grabbing" : ""} ${drag.isDragging ? "opacity-50" : ""}`}
      data-team-rank={team.rank}
    >
      <button
        type="button"
        onClick={() => onSelectTeam(team.id)}
        className="font-medium whitespace-nowrap"
      >
        {team.name}
      </button>
      {isTrial && !isActive && (
        <Badge variant="outline" className="text-[9px] px-1 py-0 border-trial text-trial shrink-0">
          Trial
        </Badge>
      )}
      {team.is_default && (
        <Badge variant="secondary" className="text-[9px] px-1 py-0 shrink-0">
          Default
        </Badge>
      )}
      {canEdit && team._actions.includes("convert") && onConvertTeam && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (confirm(`Convert "${team.display_name}" from trial to main team?`)) onConvertTeam(team.id);
          }}
          disabled={saving}
          className="ml-0.5 rounded p-0.5 hover:bg-primary/20"
          title="Convert to main team"
        >
          <ArrowRightLeft className="h-3 w-3" />
        </button>
      )}
      {canEdit && (team._actions.includes("dissolve") || team._actions.includes("delete")) && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDeleteClick(team);
          }}
          disabled={saving || checkingImpact === team.id}
          className="ml-0.5 rounded p-0.5 hover:bg-destructive/20"
          title={team._actions.includes("dissolve") ? "Dissolve trial team" : "Delete team"}
          data-action={team._actions.includes("dissolve") ? "dissolve-team" : "delete-team"}
        >
          {team._actions.includes("dissolve") ? <Trash2 className="h-3 w-3" /> : <X className="h-3 w-3" />}
        </button>
      )}
    </div>
  );
}

export function TeamTabBar({
  teams,
  activeTeamId,
  onSelectTeam,
  onDeleteTeam,
  onCreateTeam,
  onConvertTeam,
  canEdit,
  saving,
}: TeamTabBarProps) {
  const [pendingDelete, setPendingDelete] = useState<{ team: TeamPublic; impact: TeamDeleteImpact } | null>(null);
  const [checkingImpact, setCheckingImpact] = useState<string | null>(null);

  // 087: sort by rank so visual order matches persisted order.
  const regularTeams = teams.filter((t) => t.team_type === "main").sort((a, b) => a.rank - b.rank);
  const trialTeams = teams.filter((t) => t.team_type === "trial").sort((a, b) => a.rank - b.rank);

  const mainCount = regularTeams.length;
  const trialCount = trialTeams.length;

  /** Delete button handler -- fetches the delete-impact first. */
  async function handleDeleteClick(team: TeamPublic) {
    setCheckingImpact(team.id);
    try {
      const res = await api.getTeamDeleteImpact(team.id);
      // 078: always show confirmation -- including for empty teams.
      setPendingDelete({ team, impact: res.impact });
    } catch {
      if (confirm(`Delete ${team.display_name}?`)) onDeleteTeam(team.id);
    } finally {
      setCheckingImpact(null);
    }
  }

  function tab(team: TeamPublic) {
    return (
      <TeamTab
        key={team.id}
        team={team}
        isActive={team.id === activeTeamId}
        partitionSize={team.team_type === "trial" ? trialCount : mainCount}
        canEdit={canEdit}
        saving={saving}
        checkingImpact={checkingImpact}
        onSelectTeam={onSelectTeam}
        onConvertTeam={onConvertTeam}
        onDeleteClick={handleDeleteClick}
      />
    );
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {regularTeams.map(tab)}

      {trialTeams.length > 0 && (
        <>
          <div className="h-6 border-l border-border mx-1" />
          <span className="text-xs text-trial uppercase tracking-wider font-medium">Trials</span>
          {trialTeams.map(tab)}
        </>
      )}

      {trialTeams.length > 0 && (
        <span className="text-[10px] text-muted-foreground italic">
          Players in trial teams can also be on main teams
        </span>
      )}

      {canEdit && (
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => onCreateTeam(false)}
            disabled={saving}
            title="Add a team -- name is auto-generated from age group + rank"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add Team
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 border-dashed border-trial text-trial hover:bg-trial/10"
            onClick={() => onCreateTeam(true)}
            disabled={saving}
            title="Add a trial team"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Trial
          </Button>
        </div>
      )}

      {pendingDelete && (
        <TeamDeleteDialog
          team={pendingDelete.team}
          impact={pendingDelete.impact}
          onClose={() => setPendingDelete(null)}
          onConfirm={async () => {
            onDeleteTeam(pendingDelete.team.id);
            setPendingDelete(null);
          }}
        />
      )}
    </div>
  );
}
